import type { NamedPeriodPresetId } from './period-presets.ts'
import { toApiError } from './api-error.ts'
import { api } from './api.ts'

/**
 * The alert rules API: a person's saved views, behind `/alert-reports`,
 * `/alert-rules`, `/alert-rule`, `/alert-rule-test` and `/alert-preview`.
 *
 * Framework, because two apps call it: a report page creates an alert, and
 * `alerts` lists and changes them.
 */

/** The cache key for `GET /alert-rules`. Creating an alert invalidates it. */
export const ALERT_RULES_QUERY_KEY = ['alert-rules'] as const

/** The cache key for `GET /alert-reports`. */
export const ALERT_REPORTS_QUERY_KEY = ['alert-reports'] as const

/** The day names a saved week view stores, Sunday first, matching `WeekStart`. */
export const ALERT_WEEK_STARTS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
] as const

/** A day name, as a saved week view stores it. */
export type AlertWeekStart = (typeof ALERT_WEEK_STARTS)[number]

/**
 * The page a saved alert watches.
 *
 * `period`, when present, is a preset id, never `custom`. `query` is the page's other URL
 * parameters: scope, `field__operator` filters and sort. It never carries
 * `from`, `to`, `period`, `weekStart`, `page` or `limit`.
 */
export interface AlertView {
  period?: NamedPeriodPresetId
  weekStart?: AlertWeekStart
  query: string
}

/** Where the rows come from. Slice 4's draft source is not in this contract. */
export interface AlertSource {
  kind: 'report'
  report: string
  view: AlertView
}

/** How a figure is compared. `changePct` is a percent, and must be above 0. */
export const ALERT_FIGURE_OPS = ['gte', 'lte', 'changePct'] as const

/** One of {@link ALERT_FIGURE_OPS}. */
export type AlertFigureOp = (typeof ALERT_FIGURE_OPS)[number]

/** When a check sends a DM. */
export type AlertTrigger =
  | { kind: 'schedule' }
  | { kind: 'rows' }
  | { kind: 'newRows' }
  | { kind: 'figure'; figure: string; op: AlertFigureOp; value: number }

/** How often a check is due. `hour` is 0–23 in the owner's time zone. */
export type AlertSchedule =
  | { every: 'hour' }
  | { every: 'day'; hour: number }
  | { every: 'week'; weekday: number; hour: number }

/** How much of the answer the DM carries. */
export const ALERT_DETAILS = ['figures', 'counts', 'link'] as const

/** One of {@link ALERT_DETAILS}. */
export type AlertDetail = (typeof ALERT_DETAILS)[number]

/** Where an alert reaches its owner: a Slack DM, an email to their own address, or both. */
export const ALERT_CHANNELS = ['slack', 'email'] as const

/** One of {@link ALERT_CHANNELS}. */
export type AlertChannel = (typeof ALERT_CHANNELS)[number]

/** What the owner sends to create an alert. The session supplies the owner. */
export interface NewAlert {
  source: AlertSource
  trigger: AlertTrigger
  schedule: AlertSchedule
  detail: AlertDetail
  label: string | null
  /** Each channel once. The endpoint reads a body without it as `['slack']`. */
  delivery: AlertChannel[]
}

/**
 * An alert in words: the same sentences its Slack DM uses.
 *
 * The backend writes them, so a card, a preview and a DM cannot disagree.
 */
export interface AlertWords {
  view: string
  trigger: string
  schedule: string
}

/** What the hourly evaluator last recorded. Null until the first check. */
export interface AlertRuleState {
  lastCheckedAt: string | null
  lastSentAt: string | null
  lastError: string | null
}

/** One of the reader's alerts, as `GET /alert-rules` answers it. */
export interface AlertRule {
  id: string
  source: AlertSource
  trigger: AlertTrigger
  schedule: AlertSchedule
  detail: AlertDetail
  label: string | null
  delivery: AlertChannel[]
  status: 'active' | 'paused'
  activeSince: string
  createdAt: string
  updatedAt: string
  state: AlertRuleState | null
  /** The console URL of the view, the same one the DM carries. */
  link: string
  words: AlertWords
}

/** The reader's alerts, newest first, and how many one person may hold. */
export interface AlertRuleList {
  rules: AlertRule[]
  limit: number
}

/** What a rule may change after it exists. The view and the trigger never do. */
export interface AlertChange {
  id: string
  status?: 'active' | 'paused'
  label?: string | null
}

/** One figure a report already returns, and the unit a DM prints it with. */
export interface AlertFigure {
  id: string
  label: string
  unit: 'money' | 'count' | 'percent' | 'number' | 'days'
}

/**
 * The URL parameters a report's saved view may carry, from its table declaration.
 *
 * `scope` never lists `from` or `to`. `filters` lists every declared
 * `field__operator` pair. `sort` says whether the view may keep a sort.
 */
export interface AlertReportParams {
  scope: string[]
  filters: string[]
  sort: boolean
  search: boolean
}

/** The report's parameter words, shared with its DM. */
export interface AlertReportWords {
  scope?: { param: string; all: string; some: string }
  switches?: Record<string, { true: string; false: string }>
  labels: Record<string, string>
  operators: Record<string, string>
  search: string
}

/** One report the registry offers, as `GET /alert-reports` answers it. */
export interface AlertReport {
  id: string
  label: string
  path: string
  periods: string[]
  takesPeriod: boolean
  triggers: Array<'schedule' | 'rows' | 'newRows' | 'figure'>
  figures: AlertFigure[]
  hourly: boolean
  params: AlertReportParams
  words: AlertReportWords
}

/** What `POST /alert-preview` answers, whether or not it also sent the alert. */
export interface AlertPreview {
  wouldSend: boolean
  reason: string
  /** The Slack DM as it would be sent. */
  text: string
  /** The email as it would be sent, or null when the alert does not email. */
  email: { subject: string; text: string } | null
  sent: boolean
  /** Why a channel refused a send that another channel took. Empty otherwise. */
  sendErrors: string[]
  words: AlertWords
}

/**
 * Read the reports an alert can watch.
 *
 * @returns The registry entries the form is allowed to offer.
 */
export async function fetchAlertReports(): Promise<{ reports: AlertReport[] }> {
  try {
    return await api.get('alert-reports').json<{ reports: AlertReport[] }>()
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Read the reader's own alerts, newest first.
 *
 * @returns The alerts and the per-person limit.
 */
export async function fetchAlertRules(): Promise<AlertRuleList> {
  try {
    return await api.get('alert-rules').json<AlertRuleList>()
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Create an alert for the reader.
 *
 * The answer is 200 with the stored rule, not 201.
 *
 * @param alert - The view, the trigger, the schedule, the detail and the label.
 * @returns The rule as it was stored.
 */
export async function createAlert(alert: NewAlert): Promise<AlertRule> {
  try {
    const answer = await api
      .post('alert-rules', { json: alert })
      .json<{ rule: AlertRule }>()

    return answer.rule
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Pause, resume, relabel or retune one alert.
 *
 * A POST, because the CORS preflight the API answers allows no PATCH.
 *
 * @param change - The alert, and what to change on it.
 * @returns The alert as it now stands.
 */
export async function updateAlert(change: AlertChange): Promise<AlertRule> {
  try {
    const answer = await api
      .post('alert-rule', { json: change })
      .json<{ rule: AlertRule }>()

    return answer.rule
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Delete one alert.
 *
 * @param id - The alert.
 * @returns When the alert is gone.
 */
export async function deleteAlert(id: string): Promise<void> {
  try {
    await api.delete('alert-rule', { searchParams: { id } }).json()
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Run the evaluation once for the caller, with no stored state.
 *
 * Pass `send: true` to send it to the caller, on each of its channels, as well.
 *
 * @param body - The alert the form would save, and whether to send it now.
 * @returns Whether it would send, why, the DM text, and whether it was sent.
 */
export async function previewAlert(
  body: NewAlert & { send?: boolean }
): Promise<AlertPreview> {
  try {
    return await api.post('alert-preview', { json: body }).json<AlertPreview>()
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Send the reader one test DM, to prove Slack reaches them.
 *
 * @returns When Slack took the DM.
 */
export async function sendTestAlert(): Promise<void> {
  try {
    await api.post('alert-rule-test', { json: {} }).json()
  } catch (error) {
    throw await toApiError(error)
  }
}
